// ─── Public API types exposed to partners ─────────────────────────────────────
// Nothing in here mentions PandaDoc.

export interface ContractConfig {
  /** Signing token obtained from the Quantum Lends API (PART-78) */
  signingToken: string;
  /** Partner-supplied contract identifier; echoed back in every event payload */
  contractId?: string;
  /**
   * Width of the SDK's container element. Applied as CSS to the partner-
   * supplied container (NOT passed through to PandaDoc — the vendor SDK has
   * a bug where it appends "px" to string values, breaking "100%" etc.).
   *
   * - `number` → applied as `${n}px`
   * - `string` → applied verbatim (e.g. `"100%"`, `"50vw"`)
   *
   * Default: leave the partner's container styling untouched.
   */
  width?: number | string;
  /**
   * Height of the SDK's container element. Same handling as `width`.
   *
   * Default: leave the partner's container styling untouched.
   */
  height?: number | string;
  /** Enable verbose console logging for debugging. Default: false */
  debug?: boolean;
}

/** Base payload present on every Quantum contract event */
export interface ContractEventPayload {
  /** Echoed from constructor — the partner-supplied contractId, if provided */
  contractId?: string;
}

/**
 * Discriminated union of error codes surfaced via contract.error.
 *
 * **Forward-compatibility contract:** This union is intentionally non-exhaustive.
 * Additional codes may be added in any minor/patch release (e.g. `declined`,
 * `cancelled`, `network_error`) without a major version bump. Partners using
 * exhaustive `switch (code) { ... }` statements MUST include a `default` arm.
 * The `string & {}` intersection preserves autocomplete on the known codes
 * while keeping the type widening-safe for additions.
 *
 * Known codes today:
 * - `not_found` — signing token is invalid or its document is gone
 * - `verification_required` — signer must complete SMS/email verification first
 * - `verification_limit_exceeded` — signer hit the SMS retry cap; vendor refuses to proceed
 * - `loading_error` — iframe / SDK init failed (network, container, vendor outage)
 * - `unknown` — vendor signaled an exception without a specific code
 */
export type ContractErrorCode =
  | "not_found"
  | "verification_required"
  | "verification_limit_exceeded"
  | "loading_error"
  | "unknown"
  | (string & {});

/**
 * Runtime list of error codes the SDK knows about at the time of this
 * release. Useful for partners who want to narrow `code` to a known value
 * in error handlers (the type itself is intentionally non-exhaustive — see
 * `ContractErrorCode`):
 *
 * ```ts
 * contract.on('contract.error', (p) => {
 *   if (KNOWN_ERROR_CODES.includes(p.code as KnownContractErrorCode)) {
 *     // narrow + handle each known case
 *   } else {
 *     // future code we don't recognize yet — log and surface generically
 *   }
 * });
 * ```
 */
export const KNOWN_ERROR_CODES = Object.freeze([
  "not_found",
  "verification_required",
  "verification_limit_exceeded",
  "loading_error",
  "unknown",
] as const);

/** Compile-time union derived from KNOWN_ERROR_CODES (the closed v1.0.0 set). */
export type KnownContractErrorCode = (typeof KNOWN_ERROR_CODES)[number];

/** Payload for contract.error events */
export interface ContractErrorPayload extends ContractEventPayload {
  code: ContractErrorCode;
  message?: string;
}

/** Full event map — partners subscribe to these string names */
export interface ContractEventsMap {
  /** Iframe is initializing */
  "contract.loading": ContractEventPayload;
  /** Contract document is fully loaded and visible */
  "contract.loaded": ContractEventPayload;
  /** Contract document has rendered all pages */
  "contract.ready": ContractEventPayload;
  /** Signer has completed all required fields and submitted */
  "contract.completed": ContractEventPayload;
  /** An error occurred — inspect `code` for the specific failure */
  "contract.error": ContractErrorPayload;
}

export type ContractEventName = keyof ContractEventsMap;
export type ContractEventHandler<K extends ContractEventName> = (
  payload: ContractEventsMap[K],
) => void;
